import { SessionEndedError } from '../../../errors/SessionEndedError';
import { refreshSession, revokeSession, signIn } from '../clients';
import { SESSION_STATUS, SESSION_STORAGE_KEYS } from '../constants';
import { fromSsoToken, toSessionTokens } from '../mappers';
import { INITIAL_SESSION, hasFreshAccessToken, transition } from './sessionMachine';

/**
 * The one owner of the admin's session.
 *
 * It holds the session state, feeds what happens (boot, sign-in, a refresh
 * answer, another tab's change) through `transition`, and carries out the
 * effects the machine asks for. Nothing else in the app reads the tokens or
 * their storage: requests get their access token from `getAccessToken` (the
 * axios instance asks for it on every request), React reads the state with
 * `useSession`, and whatever must be cleared when a session ends registers
 * with `onSessionEnded`.
 */

/**
 * How many refreshes one request waits through: its own, and the one a refused
 * refresh hands over to when another tab stored a newer token. Anything past
 * that is not a hand-over, so the request gives up instead of looping.
 */
const MAX_REFRESH_WAITS = 3;

/**
 * The server answered about the refresh token and the answer was no: a 401 or
 * 403, or a success that carried no tokens. Retrying cannot change either, so
 * both end the session. Anything else (no answer, a 5xx) says nothing about
 * the token.
 */
const isRefusal = (error) =>
  Boolean(error?.unusableAnswer) || [401, 403].includes(error?.response?.status);

const unusableAnswer = () =>
  Object.assign(new Error('The refresh answered without tokens'), { unusableAnswer: true });

/** The part of the session the app renders from. The tokens are not in it. */
const toSnapshot = ({ status, endReason }) => Object.freeze({ status, endReason });

const safeStorage = (storage) => ({
  get(key) {
    try {
      return storage?.getItem(key) ?? null;
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      storage?.setItem(key, value);
    } catch {
      // Private mode or a full quota: the session still works until reload.
    }
  },
  remove(key) {
    try {
      storage?.removeItem(key);
    } catch {
      // As above.
    }
  },
});

/**
 * @param {Object} [deps] - injectable for tests; defaults are the real ones
 */
export const createSessionStore = ({
  clients = { signIn, refreshSession, revokeSession },
  storage = typeof window === 'undefined' ? null : window.localStorage,
  eventTarget = typeof window === 'undefined' ? null : window,
  getBuildKey = () => process.env.REACT_APP_VALID_TOKEN_UUID,
  getTurnstileToken = () => process.env.REACT_APP_TURNSTILE_TOKEN,
  now = () => Date.now(),
} = {}) => {
  const store = safeStorage(storage);
  let state = INITIAL_SESSION;
  let snapshot = toSnapshot(state);
  const subscribers = new Set();
  const endListeners = new Set();
  // One refresh per refresh token: concurrent callers share it. Each settles
  // to `{ error }` or `{}` and never rejects, so awaiting it is always safe.
  const refreshes = new Map();

  let send;
  let listening = false;

  const runRefresh = (refreshToken) => {
    if (refreshes.has(refreshToken)) return refreshes.get(refreshToken);
    const flight = clients
      .refreshSession(refreshToken)
      .then((payload) => {
        const tokens = toSessionTokens(payload, now());
        if (!tokens) throw unusableAnswer();
        // The new session is in place before the flight is released, so a
        // caller arriving in between never sees the expired one.
        send({ type: 'REFRESH_SUCCEEDED', refreshToken, tokens });
        return {};
      })
      .catch((error) => {
        if (isRefusal(error)) {
          send({
            type: 'REFRESH_REJECTED',
            refreshToken,
            storedRefreshToken: store.get(SESSION_STORAGE_KEYS.REFRESH_TOKEN),
          });
        } else {
          send({ type: 'REFRESH_FAILED', refreshToken });
        }
        return { error };
      })
      .finally(() => {
        refreshes.delete(refreshToken);
      });
    refreshes.set(refreshToken, flight);
    return flight;
  };

  const runEffect = (effect) => {
    switch (effect.type) {
      case 'persist': {
        store.set(SESSION_STORAGE_KEYS.REFRESH_TOKEN, effect.refreshToken);
        const buildKey = getBuildKey();
        if (buildKey) store.set(SESSION_STORAGE_KEYS.BUILD_KEY, buildKey);
        break;
      }
      case 'clear':
        store.remove(SESSION_STORAGE_KEYS.REFRESH_TOKEN);
        break;
      case 'refresh':
        runRefresh(effect.refreshToken);
        break;
      case 'revoke':
        clients.revokeSession(effect.refreshToken).catch(() => {});
        break;
      case 'ended':
        endListeners.forEach((listener) => {
          try {
            listener(effect.reason);
          } catch {
            // One failing teardown must not stop the others.
          }
        });
        break;
      default:
        throw new Error(`Unknown session effect: ${effect.type}`);
    }
  };

  send = (event) => {
    const { state: next, effects } = transition(state, event);
    const changed = next !== state;
    state = next;
    // A renewal changes only the tokens; keeping the snapshot lets React skip it.
    if (next.status !== snapshot.status || next.endReason !== snapshot.endReason) {
      snapshot = toSnapshot(next);
    }
    // State first, then effects: a teardown or a subscriber reading the store
    // already sees the session as it now is.
    if (changed) subscribers.forEach((notify) => notify());
    effects.forEach(runEffect);
  };

  const onStorage = (event) => {
    if (event.key !== SESSION_STORAGE_KEYS.REFRESH_TOKEN && event.key !== null) return;
    send({ type: 'STORAGE_CHANGED', refreshToken: store.get(SESSION_STORAGE_KEYS.REFRESH_TOKEN) });
  };

  /**
   * Starts following other tabs, and restores the stored session unless this
   * one already has a session. Safe to call more than once.
   */
  const start = () => {
    if (!listening) {
      eventTarget?.addEventListener('storage', onStorage);
      listening = true;
    }
    if (state.started) return;
    send({
      type: 'BOOT',
      storedRefreshToken: store.get(SESSION_STORAGE_KEYS.REFRESH_TOKEN),
      storedBuildKey: store.get(SESSION_STORAGE_KEYS.BUILD_KEY),
      buildKey: getBuildKey(),
    });
  };

  /**
   * The access token for the next request, refreshed first if it has expired.
   *
   * Rejects with `SessionEndedError` when there is no session to ask for, and
   * with the refresh's own error when the refresh could not reach the server —
   * that request fails, but the session is kept.
   */
  const getAccessToken = async () => {
    start();
    for (let attempt = 0; attempt < MAX_REFRESH_WAITS; attempt += 1) {
      if (hasFreshAccessToken(state, now())) return state.accessToken;
      if (state.status === SESSION_STATUS.SIGNED_IN) send({ type: 'ACCESS_TOKEN_EXPIRED' });

      const flight = state.refreshToken && refreshes.get(state.refreshToken);
      if (!flight) break;
      // eslint-disable-next-line no-await-in-loop
      const { error } = await flight;
      if (hasFreshAccessToken(state, now())) return state.accessToken;
      if (error && !isRefusal(error) && state.status !== SESSION_STATUS.SIGNED_OUT) throw error;
    }
    throw new SessionEndedError();
  };

  /** Email and password sign-in. Rejects with the request's error. */
  const signInWithCredentials = async ({ email, password }) => {
    const payload = await clients.signIn({
      email,
      password,
      turnstileToken: getTurnstileToken(),
    });
    const tokens = toSessionTokens(payload, now());
    if (!tokens) throw new Error('Sign-in answered without tokens');
    send({ type: 'SIGNED_IN', tokens });
  };

  /**
   * The Google SSO hand-off. Returns whether the token was usable; a malformed
   * one changes nothing.
   */
  const signInWithSsoToken = (hex) => {
    const tokens = fromSsoToken(hex, now());
    if (!tokens) return false;
    send({ type: 'SIGNED_IN', tokens });
    return true;
  };

  return {
    getState: () => state,
    /**
     * What React renders from: `{ status, endReason }`. It is the same object
     * until one of the two changes, so renewing the access token re-renders
     * nothing.
     */
    getSnapshot: () => snapshot,
    subscribe(notify) {
      subscribers.add(notify);
      return () => subscribers.delete(notify);
    },
    /** Runs `listener(reason)` every time a session ends, however it ends. */
    onSessionEnded(listener) {
      endListeners.add(listener);
      return () => endListeners.delete(listener);
    },
    start,
    getAccessToken,
    signInWithCredentials,
    signInWithSsoToken,
    logout: () => send({ type: 'LOGOUT' }),
    retry: () => send({ type: 'RETRY' }),
    /** Test seam: start from a signed-in session without a request. */
    seed(tokens) {
      send({ type: 'SIGNED_IN', tokens });
    },
    /**
     * Test seam: back to before `start`, with the storage listener and any
     * in-flight refresh dropped. Subscribers and `onSessionEnded` listeners
     * stay; whoever registered them unregisters them.
     */
    reset() {
      eventTarget?.removeEventListener('storage', onStorage);
      listening = false;
      refreshes.clear();
      state = INITIAL_SESSION;
      snapshot = toSnapshot(state);
      subscribers.forEach((notify) => notify());
    },
  };
};
